import SelectionStep from './SelectionStep';
import InitializeSelectionStep from './InitializeSelectionStep';
import { SELECTION_STEPS, SelectionStepKey } from './selectionStepsConfigs';
import FileTypesValues from '../../../interfaces/FileType';
import selectionStepsMap, { SelectionStepWithDataLabels, TEST_TYPES } from './selectionStepsConfigs';
import useSchools from '../../hooks/useSchools';
import useDemographicCategories from '../../hooks/useDemographicCategories';
import Loader from '../../reusable/Loader';
import { fileTypes } from '../../../constants';
import { getSchoolSelectionOptions } from './schoolSelection';

interface SelectionStepRendererProps {
  currentStep: {
    key: SelectionStepKey;
    label: string;
    title: string;
  };
  handleChangeStep: () => void;
  selectedData: string[];
  data: string[] | null;
  fileType: FileTypesValues;
  updateCachedData: (data: string[]) => void;
  handleSelection: (stepKey: string, isSelected: boolean, value: string[]) => void;
}
function SelectionStepRenderer({
  currentStep,
  handleChangeStep,
  selectedData,
  data,
  fileType,
  updateCachedData,
  handleSelection,
}: SelectionStepRendererProps) {
  const {
    schools: { isPending: isSchoolsPending, error: schoolsError, data: schools },
  } = useSchools();
  const {
    demographicCategories: {
      isPending: isDemographicCategoriesPending,
      error: demographicCategoriesError,
      data: demographicCategories,
    },
  } = useDemographicCategories();

  if (currentStep.key === SELECTION_STEPS.SCHOOL.key) {
    if (isSchoolsPending) return <Loader />;
    if (schoolsError || !schools) return <p>Missing schools data!</p>;

    const filteredSchools =
      fileType === fileTypes.COURSE_CATALOG ? schools : schools.filter((school) => school.is_primary_on_eoa !== false);

    const schoolOptions = getSchoolSelectionOptions(filteredSchools);
    const externalSchools = new Set(schoolOptions.filter((option) => option.isExternal).map((option) => option.value));
    const optionLabels = Object.fromEntries(schoolOptions.map((option) => [option.value, option.label]));

    return (
      <SelectionStep
        handleChangeStep={handleChangeStep}
        handleSelection={handleSelection}
        data={schoolOptions.map((option) => option.value)}
        optionLabels={optionLabels}
        selectedData={selectedData}
        selectionStepKey={SELECTION_STEPS.SCHOOL.key}
        selectionStepLabel={SELECTION_STEPS.SCHOOL.label}
        externalSchools={externalSchools}
      />
    );
  }

  if (currentStep.key === SELECTION_STEPS.DEMOGRAPHIC_TYPES.key) {
    if (isDemographicCategoriesPending) return <Loader />;
    if (demographicCategoriesError || !demographicCategories) return <p>Missing demographic categories data!</p>;

    return (
      <SelectionStep
        handleChangeStep={handleChangeStep}
        handleSelection={handleSelection}
        data={demographicCategories.map((category) => category.display_name)}
        selectedData={selectedData}
        selectionStepKey={SELECTION_STEPS.DEMOGRAPHIC_TYPES.key}
        selectionStepLabel={SELECTION_STEPS.DEMOGRAPHIC_TYPES.label}
      />
    );
  }

  if (currentStep.key === SELECTION_STEPS.TEST_TYPES.key) {
    return (
      <SelectionStep
        handleChangeStep={handleChangeStep}
        handleSelection={handleSelection}
        data={TEST_TYPES}
        selectedData={selectedData}
        selectionStepKey={SELECTION_STEPS.TEST_TYPES.key}
        selectionStepLabel={SELECTION_STEPS.TEST_TYPES.label}
      />
    );
  }

  if (!data) {
    let stepConfig = selectionStepsMap[fileType] as SelectionStepWithDataLabels;

    return (
      <InitializeSelectionStep
        selectedData={selectedData}
        selectionStepKey={currentStep.key}
        selectionStepLabel={currentStep.label}
        fetchData={stepConfig.fetchData}
        getDataLabels={stepConfig.getDataLabels}
        handleChangeStep={handleChangeStep}
        handleSelection={handleSelection}
        updateCachedData={updateCachedData}
      />
    );
  }

  return (
    <SelectionStep
      handleChangeStep={handleChangeStep}
      handleSelection={handleSelection}
      data={data}
      selectedData={selectedData}
      selectionStepKey={currentStep.key}
      selectionStepLabel={currentStep.label}
    />
  );
}

export default SelectionStepRenderer;
